// Phase 3 — client-side financial roll-up. CAPEX/OPEX come from the KB system schema
// (industry-standard $/m²), localized by a coarse country multiplier and overridable per
// paired instance. Also builds a print-to-PDF "Generate Report" summary.
import { collectReferences } from "./referencesExport";

const REGION_MULT = {
  "united states": 1.0, canada: 0.95, "united kingdom": 1.15, ireland: 1.12,
  germany: 1.12, france: 1.1, netherlands: 1.1, spain: 0.9, italy: 0.92,
  singapore: 1.25, "united arab emirates": 1.1, saudi: 1.05, australia: 1.1,
  japan: 1.15, china: 0.7, india: 0.55, brazil: 0.65, mexico: 0.7, "south africa": 0.68,
};

// US metro construction-cost indices relative to the US national average (= 1.00). City-level
// granularity so e.g. Seattle reflects a cost premium over the country baseline. Basis: RSMeans
// City Cost Index / Mortenson Construction Cost Index 2025 (Seattle 208 vs US 195 ≈ 1.07).
const US_METRO_MULT = {
  "san francisco": 1.30, "new york": 1.33, boston: 1.18, seattle: 1.07, chicago: 1.14,
  "los angeles": 1.08, portland: 1.07, denver: 1.02, washington: 1.02, philadelphia: 1.10,
  minneapolis: 1.09, miami: 0.92, houston: 0.88, dallas: 0.88, atlanta: 0.90, phoenix: 0.88,
};

export function regionMultiplier(name) {
  if (!name) return 1.0;
  const lc = name.toLowerCase();
  const metro = Object.keys(US_METRO_MULT).find((k) => lc.includes(k));
  if (metro) return US_METRO_MULT[metro];
  const hit = Object.keys(REGION_MULT).find((k) => lc.includes(k));
  return hit ? REGION_MULT[hit] : 1.0;
}

// Human-readable basis label for the cost multiplier (metro vs country).
export function regionBasis(name) {
  if (!name) return "US national baseline (no location set)";
  const lc = name.toLowerCase();
  const metro = Object.keys(US_METRO_MULT).find((k) => lc.includes(k));
  if (metro) return `${metro.replace(/\b\w/g, (c) => c.toUpperCase())} metro index (RSMeans / Mortenson 2025, vs US avg)`;
  const hit = Object.keys(REGION_MULT).find((k) => lc.includes(k));
  return hit ? `${hit.replace(/\b\w/g, (c) => c.toUpperCase())} country index (vs US baseline)` : "US national baseline";
}

export function computeFinancials(blocks, kb, geoName, overrides = {}) {
  const mult = regionMultiplier(geoName);
  const rows = (blocks || []).map((b) => {
    const sys = (kb?.systems || []).find((s) => s.id === b.systemId);
    const base = sys?.financials || { capex_per_m2: 600, opex_per_m2_yr: 70 };
    const ov = overrides[b.id] || {};
    const capexRate = ov.capex_per_m2 != null && ov.capex_per_m2 !== "" ? Number(ov.capex_per_m2) : base.capex_per_m2 * mult;
    const opexRate = ov.opex_per_m2_yr != null && ov.opex_per_m2_yr !== "" ? Number(ov.opex_per_m2_yr) : base.opex_per_m2_yr * mult;
    const area = Number(b.area) || 0;
    return {
      id: b.id, label: sys?.label || b.systemId, area,
      capexRate: Math.round(capexRate), opexRate: Math.round(opexRate),
      capex: Math.round(capexRate * area), opex: Math.round(opexRate * area),
      overridden: ov.capex_per_m2 != null || ov.opex_per_m2_yr != null,
    };
  });
  const totalCapex = rows.reduce((s, r) => s + r.capex, 0);
  const totalOpex = rows.reduce((s, r) => s + r.opex, 0);
  return { rows, totalCapex, totalOpex, mult };
}

const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const m2 = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} m²`;

// ---- Generate Report: print-to-PDF brief (scenario + metrics + mass balance + financials).
function esc(s) { return String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c])); }

// Inline monochrome SVG iconography (no external assets) for systems & crops in the PDF.
const _svg = (inner) => `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
const ICONS = {
  droplet: _svg('<path d="M12 3s5.5 5.6 5.5 9.5A5.5 5.5 0 0 1 6.5 12.5C6.5 8.6 12 3 12 3Z"/>'),
  fish: _svg('<path d="M3 12c3-4 8-5 12-3 2 1 4 3 6 3-2 0-4 2-6 3-4 2-9 1-12-3Z"/><circle cx="16" cy="11" r=".6" fill="currentColor"/>'),
  stack: _svg('<rect x="4" y="4" width="16" height="4" rx="1"/><rect x="4" y="10" width="16" height="4" rx="1"/><rect x="4" y="16" width="16" height="4" rx="1"/>'),
  house: _svg('<path d="M4 11 12 4l8 7"/><path d="M6 10v10h12V10"/>'),
  sprout: _svg('<path d="M12 20v-8"/><path d="M12 12c0-3 3-5 6-5 0 3-3 5-6 5Z"/><path d="M12 13c0-3-3-5-6-5 0 3 3 5 6 5Z"/>'),
  sun: _svg('<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19"/>'),
  box: _svg('<path d="M3 7l9-4 9 4-9 4-9-4Z"/><path d="M3 7v10l9 4 9-4V7"/>'),
  mushroom: _svg('<path d="M4 11a8 8 0 0 1 16 0Z"/><path d="M10 11v6a2 2 0 0 0 4 0v-6"/>'),
  leaf: _svg('<path d="M4 20c0-9 7-16 16-16 0 9-7 16-16 16Z"/><path d="M4 20 14 10"/>'),
  fruit: _svg('<circle cx="12" cy="14" r="6"/><path d="M12 8V4M12 4c2 0 3-1 3-2"/>'),
  root: _svg('<path d="M12 3v6"/><path d="M8 9h8l-2 8a2 2 0 0 1-4 0Z"/>'),
};
function sysIcon(label = "") {
  const s = label.toLowerCase();
  if (/aquapon|fish|aqua/.test(s)) return ICONS.fish;
  if (/hydropon|nft|dwc|deep water|aero/.test(s)) return ICONS.droplet;
  if (/vertical|rack|tower|stack/.test(s)) return ICONS.stack;
  if (/greenhouse|glass|polytunnel|tunnel/.test(s)) return ICONS.house;
  if (/mushroom|fungi/.test(s)) return ICONS.mushroom;
  if (/solar|pv|photovolt/.test(s)) return ICONS.sun;
  if (/container|warehouse|cea|chamber|indoor/.test(s)) return ICONS.box;
  if (/roof|soil|raised|bed|garden|orchard|field/.test(s)) return ICONS.sprout;
  return ICONS.leaf;
}
function cropIcon(name = "") {
  const s = name.toLowerCase();
  if (/tomato|pepper|strawberr|cucumber|fruit|melon|bean|pea|eggplant|chili/.test(s)) return ICONS.fruit;
  if (/carrot|radish|beet|turnip|root|potato|onion|garlic/.test(s)) return ICONS.root;
  if (/mushroom|fungi/.test(s)) return ICONS.mushroom;
  return ICONS.leaf;
}
const CLASSIFY_COLORS = { roof: "#f5a623", ground: "#34c759", podium: "#ff9f0a", interior: "#af52de", exterior: "#0a84ff" };
const CHART_PALETTE = ["#51aafe", "#34c759", "#ff9f0a", "#af52de", "#ff375f", "#5ac8fa", "#ffd60a", "#30d158", "#bf5af2", "#64d2ff"];

// Horizontal bar chart (SVG). items = [{ label, value }]. Pure markup, no libraries.
function barChartSVG(items, unit) {
  const rows = items.filter((d) => d.value > 0);
  if (!rows.length) return '<div class="empty">No data yet</div>';
  const max = Math.max(...rows.map((d) => d.value));
  const rh = 26, w = 300, lblW = 0;
  const h = rows.length * rh;
  const bars = rows.map((d, i) => {
    const bw = Math.max(2, (d.value / max) * (w - 120));
    const c = CHART_PALETTE[i % CHART_PALETTE.length];
    return `<g transform="translate(0,${i * rh})">
      <text x="0" y="15" font-size="11" fill="#1D1D1F">${esc(d.label)}</text>
      <rect x="120" y="5" width="${bw}" height="13" rx="3" fill="${c}"/>
      <text x="${124 + bw}" y="15" font-size="10.5" fill="#6E6E73">${Math.round(d.value).toLocaleString()}${unit ? ` ${unit}` : ""}</text>
    </g>`;
  }).join("");
  return `<svg width="100%" viewBox="0 0 ${w + 120} ${h}" preserveAspectRatio="xMinYMin meet">${bars}</svg>`;
}

// Donut chart (SVG) with a side legend. items = [{ label, value }].
function donutChartSVG(items, unit, fmt = (n) => Math.round(n).toLocaleString()) {
  const rows = items.filter((d) => d.value > 0);
  if (!rows.length) return '<div class="empty">No data yet</div>';
  const total = rows.reduce((s, d) => s + d.value, 0) || 1;
  const cx = 60, cy = 60, r = 46, sw = 22;
  const C = 2 * Math.PI * r;
  let off = 0;
  const arcs = rows.map((d, i) => {
    const frac = d.value / total;
    const c = CHART_PALETTE[i % CHART_PALETTE.length];
    const seg = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${c}" stroke-width="${sw}" stroke-dasharray="${(frac * C).toFixed(2)} ${(C - frac * C).toFixed(2)}" stroke-dashoffset="${(-off * C).toFixed(2)}" transform="rotate(-90 ${cx} ${cy})"/>`;
    off += frac; return seg;
  }).join("");
  const legend = rows.map((d, i) => `<div class="lg"><i style="background:${CHART_PALETTE[i % CHART_PALETTE.length]}"></i>${esc(d.label)} · ${fmt(d.value)}${unit ? ` ${unit}` : ""}</div>`).join("");
  return `<div class="donut"><svg width="120" height="120" viewBox="0 0 120 120">${arcs}<text x="${cx}" y="${cy + 4}" text-anchor="middle" font-size="13" font-weight="800" fill="#1D1D1F">${fmt(total)}</text></svg><div class="legend">${legend}</div></div>`;
}

export function generateReport({ geo, kpis, blocks, kb, financials, opportunity, overrides = {}, suppliers = {}, projectImage = null, surfaceTags = [], weather = null, branding = {}, occupancy = null, buildingTypology = null, scope = null, contextShade = null, zoneAllocations = {}, resourceInputs = {}, selections = {} }) {
  const k = kpis || {};
  const bal = k.balance || {};
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const finRows = (financials?.rows || []).map((r) => `
    <tr><td>${esc(r.label)}</td><td>${m2(r.area)}</td><td>${usd(r.capexRate)}/m²</td><td>${usd(r.capex)}</td><td>${usd(r.opex)}/yr</td></tr>`).join("");
  const cropLabel = (cid) => esc((kb?.crops || []).find((c) => c.id === cid)?.label || cid);

  // Grouped Systems (icon + area + instance count), aggregated across drafted blocks.
  const sysAgg = {};
  (blocks || []).forEach((b) => {
    const sys = (kb?.systems || []).find((s) => s.id === b.systemId);
    const label = sys?.label || b.systemId || "Unassigned";
    if (!sysAgg[label]) sysAgg[label] = { label, area: 0, count: 0 };
    sysAgg[label].area += Number(b.area) || 0;
    sysAgg[label].count += 1;
  });
  const systemCards = Object.values(sysAgg).map((s) => `
    <div class="card"><span class="ic">${sysIcon(s.label)}</span><div><div class="ct">${esc(s.label)}</div>
    <div class="cs">${m2(s.area)} · ${s.count} instance${s.count === 1 ? "" : "s"}</div></div></div>`).join("")
    || '<div class="empty">No systems drafted yet</div>';

  // Grouped Crops — allocated area + REAL annual yield and farm-gate revenue per crop, read from
  // the crop table per zone (NOT a single blended value). Prices are CAD (AAFC 2024); USD = CAD ÷ FX.
  const CAD_PER_USD = 1.37;
  const cropMetric = (crop, sys) => {
    const dbs = sys?.db_systems || [];
    const key = dbs.find((lb) => crop?.systems?.[lb]);
    return key ? crop.systems[key] : null;
  };
  const cropAgg = {};
  (blocks || []).forEach((b) => {
    const sys = (kb?.systems || []).find((s) => s.id === b.systemId);
    Object.entries(b.allocations || {}).forEach(([cid, pct]) => {
      if (!pct) return;
      const crop = (kb?.crops || []).find((x) => x.id === cid);
      const m = cropMetric(crop, sys);
      const blkArea = (Number(b.area) || 0) * (Number(pct) / 100);
      // Prefer a consultant per-instance override, else the table's authoritative annual yield for
      // this zone (indoor 110, herbs 108, greenhouse tomato 47.6 kg/m²·yr), else per-cycle × cycles.
      const annualPerM2 = (b.overrides && b.overrides.yield != null) ? (Number(b.overrides.yield) * 4)
        : (m && m.yield_annual != null ? Number(m.yield_annual)
          : (m && m.yield != null && m.cycle ? Number(m.yield) * Math.max(1, Math.min(26, 365 / m.cycle)) : 0));
      const yKg = annualPerM2 * blkArea;
      const priceCad = (m && m.market_value != null) ? Number(m.market_value) : (crop?.market_value != null ? Number(crop.market_value) : null);
      if (!cropAgg[cid]) cropAgg[cid] = { cid, area: 0, count: 0, yieldKg: 0, priceCad, revenueCad: 0 };
      cropAgg[cid].area += blkArea;
      cropAgg[cid].count += 1;
      cropAgg[cid].yieldKg += yKg;
      if (priceCad != null) { cropAgg[cid].revenueCad += yKg * priceCad; cropAgg[cid].priceCad = priceCad; }
    });
  });
  const totalYieldFromCrops = Object.values(cropAgg).reduce((s, c) => s + (c.yieldKg || 0), 0);
  const totalRevenueCad = Object.values(cropAgg).reduce((s, c) => s + (c.revenueCad || 0), 0);
  const totalRevenueUsd = totalRevenueCad / CAD_PER_USD;
  const cropCards = Object.values(cropAgg).map((c) => {
    const name = (kb?.crops || []).find((x) => x.id === c.cid)?.label || c.cid;
    return `<div class="card"><span class="ic">${cropIcon(name)}</span><div><div class="ct">${esc(name)}</div>
    <div class="cs">${m2(c.area)} · ${Math.round(c.yieldKg).toLocaleString()} kg/yr</div></div></div>`;
  }).join("") || '<div class="empty">No crops flagged yet</div>';

  // Surface classification summary (area by category, from manual + auto tags).
  const surfAgg = {};
  (surfaceTags || []).forEach((t) => { const c = t.category || "other"; surfAgg[c] = (surfAgg[c] || 0) + (Number(t.area) || 0); });
  const surfChips = Object.entries(surfAgg).map(([c, a]) => `
    <span class="chip"><i style="background:${CLASSIFY_COLORS[c] || "#8e8e93"}"></i>${esc(c[0].toUpperCase() + c.slice(1))} · ${Math.round(a).toLocaleString()} m²</span>`).join("");

  // Whether the consolidated costing page (and any cost figures elsewhere) is included — toggled
  // from Report details (branding.includeCosting, default true).
  const includeCosting = branding.includeCosting !== false;
  // Deep per-instance array: every drafted block with its granular geometry, crop split & overrides.
  const instanceRows = (blocks || []).map((b) => {
    const sys = (kb?.systems || []).find((s) => s.id === b.systemId);
    const ov = overrides[b.id] || {};
    const crops = Object.entries(b.allocations || {}).map(([cid, pct]) => `${cropLabel(cid)}${pct ? ` (${Math.round(pct)}%)` : ""}`).join(", ") || "—";
    const ovParts = [];
    if (includeCosting && ov.capex_per_m2 != null && ov.capex_per_m2 !== "") ovParts.push(`CAPEX ${usd(ov.capex_per_m2)}/m²`);
    if (includeCosting && ov.opex_per_m2_yr != null && ov.opex_per_m2_yr !== "") ovParts.push(`OPEX ${usd(ov.opex_per_m2_yr)}/m²·yr`);
    if (b.overrides) Object.entries(b.overrides).forEach(([kk, vv]) => { if (vv != null && vv !== "") ovParts.push(`${esc(kk)} ${esc(vv)}`); });
    return `<tr>
      <td><span class="tic">${sysIcon(sys?.label || "")}</span>${esc(sys?.label || b.systemId)}</td>
      <td>${esc(b.locationLabel || b.locationId || "—")}</td>
      <td>${m2(b.area)}</td>
      <td>${b.height ? `${b.height} m` : "—"}</td>
      <td>${b.volume ? `${Math.round(b.volume).toLocaleString()} m³` : "—"}</td>
      <td>${crops}</td>
      <td>${suppliers[b.systemId] ? esc(suppliers[b.systemId].name) : "—"}</td>
      <td>${ovParts.join(" · ") || "—"}</td>
    </tr>`;
  }).join("");

  // Zones — list view data + rough payback (computed before the KPI grid that references them).
  const zoneMap = {};
  (blocks || []).forEach((b) => {
    const zid = b.zoneId || b.locationId || "unzoned";
    if (!zoneMap[zid]) zoneMap[zid] = { zid, label: b.locationLabel || b.locationId || "Zone", area: 0, systems: {}, crops: {} };
    const z = zoneMap[zid];
    z.area += Number(b.area) || 0;
    const sl = (kb?.systems || []).find((s) => s.id === b.systemId)?.label || b.systemId;
    z.systems[sl] = (z.systems[sl] || 0) + 1;
    Object.entries(b.allocations || {}).forEach(([cid, pct]) => { if (pct) z.crops[cropLabel(cid)] = true; });
  });
  const zoneCount = Object.keys(zoneMap).length;
  const annualYield = Math.round(k.total_yield || totalYieldFromCrops || 0);
  const annualRevenueUsd = Math.round(totalRevenueUsd);
  const annualRevenueCad = Math.round(totalRevenueCad);
  // Payback is computed in USD (system CAPEX/OPEX benchmarks are USD); crop revenue is converted
  // from the AAFC CAD farm-gate prices at the stated FX.
  const annualNet = annualRevenueUsd - (financials?.totalOpex || 0);
  const paybackYrs = annualNet > 0 && financials?.totalCapex > 0 ? (financials.totalCapex / annualNet) : null;
  const paybackTxt = paybackYrs ? `${paybackYrs.toFixed(1)} yrs` : "Not reached (under stated assumptions)";

  // Embodied structural carbon (disclosed, NOT netted) — from each system's embodied_carbon_kgm2
  // baseline × its drafted area, amortized over an editable design life.
  const DESIGN_LIFE_YRS = Number(resourceInputs?.structuralLifeYrs) || 60;
  let embodiedTotalKg = 0;
  (blocks || []).forEach((b) => {
    const sys = (kb?.systems || []).find((s) => s.id === b.systemId);
    const ec = sys?.baselines?.embodied_carbon_kgm2?.value;
    if (ec) embodiedTotalKg += Number(ec) * (Number(b.area) || 0);
  });
  const embodiedAnnualKg = embodiedTotalKg > 0 ? Math.round(embodiedTotalKg / DESIGN_LIFE_YRS) : 0;

  const sysInstanceCount = (blocks || []).length;
  const cropCount = Object.keys(cropAgg).length;
  const kpiCards = [
    ["Total Yield", `${annualYield.toLocaleString()} kg/yr`],
    ["Cultivated Area", m2(k.total_area || 0)],
    ["Allocation Zones", `${zoneCount}`],
    ["System Instances", `${sysInstanceCount}`],
    ["Crops", `${cropCount}`],
    ["Water Recovery", `${Math.round(k.water_recovery || 0)}%`],
    ["Electricity Offset", `${Math.round(k.energy_offset || 0)}%`],
    ["Recovered Heat", `${Math.round(bal.energy?.heat_recovered_kWh || 0).toLocaleString()} kWh/yr`],
    ["Net Carbon (operational)", `${Math.round(bal.carbon?.net_kg || 0).toLocaleString()} kg CO₂e/yr`],
  ].map(([l, v]) => `<div class="kpi"><div class="v">${v}</div><div class="l">${l}</div></div>`).join("");
  const loopRow = (label, val) => `<tr><td>${label}</td><td>${val}</td></tr>`;

  const site = [geo?.name, (geo?.lat != null && geo?.lon != null) ? `${(+geo.lat).toFixed(3)}, ${(+geo.lon).toFixed(3)}` : null].filter(Boolean).join(" · ");
  const climate = weather?.dli ? `Site DLI ≈ ${weather.dli} mol/m²·d (${esc(weather.source || "est.")})` : "";

  // Branding (client-ready cover): title / client / studio logo (base64 PNG).
  const title = branding.coverTitle || geo?.name || "Project";
  const client = branding.clientName || "";
  const logo = branding.logo || null;

  // Yield-by-crop (area-weighted split of the modeled total yield) + CAPEX-by-system charts.
  // Yield-by-crop uses each crop's REAL modeled annual yield (crop table per zone), not an
  // area-weighted split of one blended total — so lettuce, herbs and tomato differ correctly.
  const yieldItems = Object.values(cropAgg).map((c) => ({
    label: (kb?.crops || []).find((x) => x.id === c.cid)?.label || c.cid,
    value: c.yieldKg || 0,
  })).sort((a, b) => b.value - a.value).slice(0, 8);
  const capexBySys = {};
  (financials?.rows || []).forEach((r) => { capexBySys[r.label] = (capexBySys[r.label] || 0) + r.capex; });
  const capexItems = Object.entries(capexBySys).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  const showYield = yieldItems.some((d) => d.value > 0);
  const showCapex = capexItems.some((d) => d.value > 0);
  // Per-crop pricing + revenue rows — lives on the dedicated costing page so money data is omittable.
  const priceRows = Object.values(cropAgg).filter((c) => c.priceCad != null).map((c) => {
    const name = (kb?.crops || []).find((x) => x.id === c.cid)?.label || c.cid;
    return `<tr><td>${esc(name)}</td><td>$${(c.priceCad / CAD_PER_USD).toFixed(2)}</td><td>CA$${c.priceCad.toFixed(2)}</td><td>${Math.round(c.yieldKg).toLocaleString()} kg/yr</td><td>$${Math.round(c.revenueCad / CAD_PER_USD).toLocaleString()} (CA$${Math.round(c.revenueCad).toLocaleString()})</td></tr>`;
  }).join("") || '<tr><td colspan="5">No priced crops</td></tr>';

  // Full reference appendix — ONLY the sources actually behind THIS report (systems/crops/
  // assumptions in use), grouped. Not the entire engine data bank.
  const _allRefs = collectReferences();
  const _sysLabels = new Set((blocks || []).map((b) => (kb?.systems || []).find((s) => s.id === b.systemId)?.label).filter(Boolean));
  const _cropLabels = new Set(Object.keys(cropAgg).map((cid) => (kb?.crops || []).find((c) => c.id === cid)?.label).filter(Boolean));
  // Waste-to-resource citations appear ONLY when the matching loop was actually LOCKED by the user
  // (not off a stray heat/kWh value) — so unselected pathways (BSF, AD, struvite…) are never listed.
  const _lockedResIds = Object.keys(resourceInputs?.locked || {}).filter((id) => (resourceInputs.locked || {})[id]);
  const _lockedResBlob = (kb?.resources || []).filter((r) => _lockedResIds.includes(r.id)).map((r) => `${r.label || ""} ${r.tech || ""} ${r.output || ""}`.toLowerCase());
  const _hasResources = _lockedResBlob.length > 0;
  const _hasSolar = (bal.energy?.solar_kWh || 0) > 0 || (bal.energy?.solar_thermal_kWh || 0) > 0 || (bal.energy?.heat_recovered_kWh || 0) > 0;
  const _matchAny = (txt, set) => { const t = (txt || "").toLowerCase(); return [...set].some((l) => t.includes(l.toLowerCase()) || l.toLowerCase().includes(t)); };
  const _matchLocked = (needle) => { const n = (needle || "").toLowerCase().split(" (")[0].trim(); return n ? _lockedResBlob.some((b) => b.includes(n.split(" ")[0]) || n.includes(b.split(" ")[0])) : false; };
  const usedRefs = _allRefs.filter((r) => {
    if (r.group.startsWith("Waste")) return _hasResources && _matchLocked(r.backs);
    if (r.group.startsWith("Mass")) {
      // Drop solar / PV generation sources (e.g. the Canada CER cite) unless a solar/heat loop exists.
      const t = `${r.citation} ${r.url} ${r.backs}`.toLowerCase();
      const isSolar = /solar|photovolt|\bpv\b|pvwatts|cer-rec|pv-t/.test(t);
      if (isSolar && !_hasSolar) return false;
      return true;
    }
    if (r.group.startsWith("Per-system")) return _sysLabels.size === 0 ? false : _matchAny(r.backs, _sysLabels);
    if (r.group.startsWith("Baseline")) return _matchAny(r.backs, _sysLabels) || _matchAny(r.backs, _cropLabels) || _matchAny(r.citation, _sysLabels) || _matchAny(r.citation, _cropLabels);
    return true;
  });
  const _refGroups = [...new Set(usedRefs.map((r) => r.group))];
  const refRows = _refGroups.map((g) => {
    const items = usedRefs.filter((r) => r.group === g).map((r) => `
      <li><strong>${esc(r.backs)}</strong> — ${esc(r.citation)}${r.url ? ` · <span class="doi">${esc(r.url)}</span>` : ""}</li>`).join("");
    return `<li class="refgroup">${esc(g)}</li>${items}`;
  }).join("");

  // Zones — list view rows (zoneMap + payback computed earlier, above the KPI grid).
  const zoneList = Object.values(zoneMap).map((z) => {
    const alloc = zoneAllocations?.[z.zid];
    const sysTxt = Object.entries(z.systems).map(([l, n]) => `${esc(l)}${n > 1 ? ` ×${n}` : ""}`).join(", ") || "—";
    const cropTxt = Object.keys(z.crops).join(", ") || "—";
    // Single source of truth = drafted block area. A manual zone target, if different, is shown only
    // as a secondary note so one unit never reports three different areas.
    const targetTxt = (alloc != null && Math.round(alloc) !== Math.round(z.area))
      ? ` <span style="color:#86868B">(target ${Math.round(alloc).toLocaleString()} m²)</span>` : "";
    return `<tr><td><strong>${esc(z.label)}</strong></td>
      <td>${m2(z.area)}${targetTxt}</td>
      <td>${sysTxt}</td><td>${cropTxt}</td></tr>`;
  }).join("") || '<tr><td colspan="4">No allocation zones defined yet</td></tr>';

  // Assumptions & site basis rows.
  const typLabel = buildingTypology ? ((kb?.typologies || []).find((t) => t.id === buildingTypology)?.label || buildingTypology) : null;
  const regionTxt = `${(financials?.mult ?? 1).toFixed(2)}×`;
  const srcLabel = (s) => { const x = (s || "").toLowerCase(); return (x === "api" || x.includes("open-meteo")) ? "Open-Meteo API" : (s || "estimate"); };
  const hasCoords = geo?.lat != null && geo?.lon != null;
  const gridF = bal.carbon?.grid_factor_kg_per_kwh;
  const assumeRows = [
    ["Location", site || "—"],
    ["Coordinates", hasCoords ? `${(+geo.lat).toFixed(4)}, ${(+geo.lon).toFixed(4)}` : `<span style="color:#b00020">Not pinned — pin the exact site; DLI falls back to a city-level default</span>`],
    ["Climate — daylight", weather?.dli ? `Site DLI ≈ ${weather.dli} mol/m²·day${hasCoords ? "" : " (city-level default)"} (${esc(srcLabel(weather?.source))})` : "Not resolved"],
    ["Climate — source", esc(srcLabel(weather?.source))],
    ["Building typology", typLabel ? esc(typLabel) : "—"],
    ["Estimated occupancy", occupancy != null ? `${Number(occupancy).toLocaleString()} people` : "—"],
    ["Analysis scope", scope ? esc(scope[0].toUpperCase() + scope.slice(1)) : "—"],
    ["Contextual overshadowing", contextShade != null ? `${Math.round(contextShade * 100)}% of envelope (from imported context + sun path)` : "No context massing imported (0%)"],
    ["Electricity grid factor", gridF != null ? `${gridF} kg CO₂e/kWh${bal.carbon?.grid_province ? ` (${esc(bal.carbon.grid_province)})` : ""} — editable` : "—"],
    ["Structural design life (embodied basis)", `${DESIGN_LIFE_YRS} yr`],
  ].map(([l, v]) => `<tr><td>${l}</td><td>${v}</td></tr>`).join("");

  // All cost / price / revenue / payback data — one self-contained page the user can omit via the
  // "Include costing page" toggle in Report details (includeCosting, defined above).
  const costingSection = includeCosting ? `<!-- COSTING START — all cost / price / revenue / payback data is consolidated on this page so it can be included or omitted as a single block -->
  <section class="costing">
  <h2>Financials, costing &amp; payback (localized ×${financials?.mult ?? 1})</h2>
  <p style="font-size:12px;color:#6E6E73;margin-bottom:10px">All cost, price, revenue and payback data is consolidated on this page so it can be kept or omitted as one section. CAPEX/OPEX benchmarks are USD; crop prices are AAFC 2024 (CAD), shown in CAD &amp; USD at ${CAD_PER_USD} CAD/USD. Cost basis: regional multiplier ${regionTxt} — ${esc(regionBasis(geo?.name))}.</p>
  ${showCapex ? `<div class="charts" style="grid-template-columns:1fr"><div class="chartbox"><div class="ch">CAPEX by system</div>${donutChartSVG(capexItems, "", (n) => usd(n))}</div></div>` : ""}
  <h3 class="h3">Per-crop farm-gate value</h3>
  <table><tr><th>Crop</th><th>Price (USD/kg)</th><th>Price (CAD/kg)</th><th>Modeled yield</th><th>Revenue (USD / CAD)</th></tr>${priceRows}</table>
  <h3 class="h3">CAPEX &amp; OPEX by instance</h3>
  <table>
    <tr><th>Instance</th><th>Area</th><th>CAPEX rate</th><th>CAPEX</th><th>OPEX</th></tr>${finRows || '<tr><td colspan="5">No instances yet</td></tr>'}
  </table>
  <div class="totals">
    <div><span>Total CAPEX</span><strong>${usd(financials?.totalCapex)}</strong></div>
    <div><span>Annual OPEX</span><strong>${usd(financials?.totalOpex)}/yr</strong></div>
    <div><span>Est. annual revenue</span><strong>${usd(annualRevenueUsd)}/yr</strong><div style="font-size:11px;color:#86868B;letter-spacing:0;text-transform:none">CA$${annualRevenueCad.toLocaleString()}/yr · ${CAD_PER_USD} CAD/USD</div></div>
    <div><span>Rough payback</span><strong>${paybackTxt}</strong></div>
  </div>
  <div class="note"><strong>How the financials are derived.</strong> CAPEX ($/m²) and OPEX ($/m²·yr) come from the engine's per-system benchmark library (vendor and industry figures — see Appendix), localized by the regional cost multiplier (${regionTxt} — ${esc(regionBasis(geo?.name))}) and any per-instance overrides you entered. <strong>Payback</strong> is a rough screening estimate: annual revenue = the sum over crops of each crop's modeled annual yield × its farm-gate price from the crop table (AAFC 2024, CAD), converted to USD at ${CAD_PER_USD} CAD/USD = ${usd(annualRevenueUsd)}/yr (CA$${annualRevenueCad.toLocaleString()}); minus annual OPEX; payback = total CAPEX ÷ annual net. Real produce prices, yields, financing, labour and operating costs vary widely.</div>
  <div class="disclaimer"><strong>Disclaimer.</strong> CAPEX, OPEX and payback are order-of-magnitude planning estimates, <strong>not quotations or financial advice</strong>. They should be verified with a qualified quantity surveyor / cost estimator and firm supplier pricing. Detailed engineering, structural review and local regulatory approval are required before any investment or construction decision.</div>
  </section>
  <!-- COSTING END -->` : "";

  const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Resource Circularity Engine — ${esc(title)} — ${esc(date)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1D1D1F;padding:40px 46px}
  .brand{font-size:11px;letter-spacing:3px;color:#51aafe;font-weight:800;text-transform:uppercase}
  h1{font-size:30px;letter-spacing:-1px;margin:6px 0 2px}.sub{color:#6E6E73;font-size:14px}
  h2{font-size:12px;letter-spacing:1.5px;color:#86868B;text-transform:uppercase;margin:26px 0 12px;border-bottom:1px solid #eee;padding-bottom:6px}
  .h3{font-size:12px;letter-spacing:1px;color:#86868B;text-transform:uppercase;margin:20px 0 8px}
  @page{size:A4;margin:0}
  @media print{body{padding:16mm 14mm}h2{page-break-after:avoid}table,.kpis,.cards,.cover{page-break-inside:avoid}.costing{page-break-before:always}}
  .costing{margin-top:26px}
  .cover{display:flex;gap:20px;align-items:flex-start;margin:14px 0 8px}
  .cover .meta{flex:1}.hero{width:340px;height:200px;object-fit:cover;border-radius:14px;border:1px solid #eee;background:#f5f7f9}
  .meta .site{font-size:13px;color:#333;margin-top:10px}.meta .clim{font-size:12px;color:#86868B;margin-top:3px}
  .kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
  .kpi{border:1px solid #eee;border-radius:12px;padding:14px}.kpi .v{font-size:19px;font-weight:800;letter-spacing:-.5px}.kpi .l{font-size:10px;letter-spacing:1px;color:#86868B;text-transform:uppercase;margin-top:4px}
  .cards{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}
  .card{display:flex;gap:11px;align-items:center;border:1px solid #eee;border-radius:12px;padding:11px 13px}
  .card .ic{color:#51aafe;display:flex}.card .ct{font-size:13.5px;font-weight:700}.card .cs{font-size:11.5px;color:#86868B;margin-top:2px}
  .empty{font-size:12px;color:#86868B;padding:8px 2px}
  .logo{max-height:46px;max-width:180px;margin-bottom:8px;object-fit:contain}
  .charts{display:grid;grid-template-columns:1fr 1fr;gap:20px}.chartbox{border:1px solid #eee;border-radius:12px;padding:14px}.chartbox .ch{font-size:11px;letter-spacing:.6px;color:#86868B;text-transform:uppercase;margin-bottom:10px}
  .donut{display:flex;gap:14px;align-items:center}.donut .legend{font-size:11px;color:#333}.donut .lg{display:flex;align-items:center;gap:7px;padding:2px 0}.donut .lg i{width:10px;height:10px;border-radius:3px;display:inline-block}
  .chips{display:flex;flex-wrap:wrap;gap:8px}.chip{display:inline-flex;align-items:center;gap:7px;font-size:12px;color:#333;border:1px solid #eee;border-radius:999px;padding:5px 11px}.chip i{width:10px;height:10px;border-radius:3px;display:inline-block}
  table{width:100%;border-collapse:collapse;font-size:12.5px}td,th{text-align:left;padding:8px 6px;border-bottom:1px solid #f0f0f0;vertical-align:top}th{font-size:10px;letter-spacing:1px;color:#86868B;text-transform:uppercase}
  .tic{color:#51aafe;display:inline-flex;vertical-align:-4px;margin-right:6px}
  .totals{display:flex;gap:24px;margin-top:14px}.totals div span{display:block;font-size:10px;letter-spacing:1px;color:#86868B;text-transform:uppercase}.totals div strong{font-size:20px}
  .note{font-size:11.5px;line-height:1.6;color:#6E6E73;background:#f7f9fb;border:1px solid #eef1f4;border-radius:10px;padding:12px 14px;margin-top:12px}
  .disclaimer{font-size:11px;line-height:1.6;color:#8a6d3b;background:#fdf6e9;border:1px solid #f3e6c8;border-radius:10px;padding:12px 14px;margin-top:14px}
  ul.refs{list-style:none;font-size:11px;line-height:1.7;color:#444}ul.refs li{padding:3px 0;border-bottom:1px solid #f4f4f4}.doi{color:#51aafe}ul.refs li.refgroup{font-size:10px;letter-spacing:1px;color:#86868B;text-transform:uppercase;font-weight:700;border-bottom:none;margin-top:12px;padding-bottom:2px}
  footer{margin-top:34px;padding-top:14px;border-top:1px solid #eee;font-size:11px;color:#86868B;display:flex;justify-content:space-between}
</style></head><body>
  <div class="brand">Resource Circularity Engine</div>
  <div class="cover">
    <div class="meta">
      ${logo ? `<img class="logo" src="${logo}" alt="logo"/>` : ""}
      <h1>${esc(title)}</h1>
      <div class="sub">Building-Integrated Agriculture · Conceptual Feasibility Report</div>
      ${client ? `<div class="site">Prepared for ${esc(client)}</div>` : ""}
      ${site ? `<div class="site">${esc(site)}</div>` : ""}
      <div class="clim">Exported ${esc(date)}${climate ? ` · ${climate}` : ""}</div>
    </div>
    ${projectImage ? `<img class="hero" src="${projectImage}" alt="Project massing"/>` : ""}
  </div>
  <div class="disclaimer" style="margin-top:12px"><strong>Estimates only.</strong> Every figure in this report — yields, resource balances, carbon${includeCosting ? ", CAPEX, OPEX and payback" : ""} — is a conceptual, early-stage estimate for feasibility screening. Treat them as planning indicators to be confirmed by a qualified${includeCosting ? " cost estimator," : ""} engineer and supplier quotations before any decision.</div>
  <h2>Performance at a glance</h2><div class="kpis">${kpiCards}</div>
  <h2>Assumptions &amp; site basis</h2>
  <p style="font-size:12px;color:#6E6E73;margin-bottom:8px">The modeled results rest on the location, climate and building inputs below. Change any of these in the studio and the figures update accordingly.</p>
  <table><tr><th>Parameter</th><th>Value used</th></tr>${assumeRows}</table>
  ${showYield ? `<h2>Yield by crop</h2><div class="charts" style="grid-template-columns:1fr">
    <div class="chartbox"><div class="ch">Est. yield by crop (modeled annual)</div>${barChartSVG(yieldItems, "kg/yr")}</div>
  </div>` : ""}
  <h2>Systems</h2><div class="cards">${systemCards}</div>
  <h2>Crops</h2><div class="cards">${cropCards}</div>
  ${surfChips ? `<h2>Surface classification</h2><div class="chips">${surfChips}</div>` : ""}
  <h2>Allocation zones (${zoneCount})</h2><table>
    <tr><th>Zone</th><th>Area</th><th>Systems</th><th>Crops</th></tr>${zoneList}
  </table>
  <h2>Mass &amp; Energy Loops</h2><table>
    <tr><th>Loop</th><th>Value</th></tr>
    ${loopRow("Water supply", `${Math.round(bal.water?.supply_L || 0).toLocaleString()} L/yr`)}
    ${loopRow("Water demand", `${Math.round(bal.water?.demand_L || 0).toLocaleString()} L/yr`)}
    ${loopRow("Water surplus", `${Math.round(bal.water?.surplus_L || 0).toLocaleString()} L/yr`)}
    ${loopRow("Electricity supply (PV + biogas)", `${Math.round(bal.energy?.supply_kWh || 0).toLocaleString()} kWh/yr`)}
    ${loopRow("Electricity demand (lighting + HVAC)", `${Math.round(bal.energy?.demand_kWh || 0).toLocaleString()} kWh/yr`)}
    ${loopRow("Recovered heat (thermal — separate grade)", `${Math.round(bal.energy?.heat_recovered_kWh || 0).toLocaleString()} kWh/yr`)}
  </table>
  <p style="font-size:11.5px;color:#6E6E73;margin-top:6px">Recovered ventilation / condenser / solar heat is reported as its own thermal line — a lower-grade energy stream that does <strong>not</strong> offset LED electricity, so the electricity offset reflects only electrical generation.</p>
  <h2>Carbon breakdown</h2><table>
    <tr><th>Component</th><th>Basis</th><th>kg CO₂e/yr</th></tr>
    <tr><td>Avoided — displaced conventional supply chain</td><td>${Math.round(bal.carbon?.gross_conventional_kg || 0).toLocaleString()} kg grown &amp; freighted · ${Math.round((bal.carbon?.loss_fraction || 0) * 100)}% supply-chain loss · ${bal.carbon?.transport_factor_kg_per_kg != null ? `${bal.carbon.transport_factor_kg_per_kg} kgCO₂e/kg (cited)` : `${(bal.carbon?.food_miles_km || 0).toLocaleString()} km × ${bal.carbon?.freight_ef || 0} kgCO₂e/t·km × reefer +${Math.round((bal.carbon?.reefer_uplift || 0) * 100)}%`}</td><td style="color:#1e8f42">+${Math.round(bal.carbon?.avoided_kg || 0).toLocaleString()}</td></tr>
    <tr><td>On-site operational footprint</td><td>${Math.round(bal.energy?.demand_kWh || 0).toLocaleString()} kWh/yr electricity × ${bal.carbon?.grid_factor_kg_per_kwh ?? 0.018} kgCO₂e/kWh${bal.carbon?.grid_province ? ` (${esc(bal.carbon.grid_province)} grid)` : ""}</td><td style="color:#b00020">−${Math.round(bal.carbon?.footprint_kg || 0).toLocaleString()}</td></tr>
    <tr><td><strong>Net carbon (operational basis)</strong></td><td>Avoided − operational footprint</td><td><strong>${Math.round(bal.carbon?.net_kg || 0).toLocaleString()}</strong></td></tr>
    <tr><td style="color:#8a6d3b">Embodied structure (amortized — disclosed, NOT netted)</td><td style="color:#8a6d3b">${embodiedTotalKg > 0 ? `${Math.round(embodiedTotalKg).toLocaleString()} kg embodied ÷ ${DESIGN_LIFE_YRS} yr design life` : "No embodied-carbon data in the selected systems — enter below-grade / structural quantities for a whole-life figure"}</td><td style="color:#8a6d3b">${embodiedAnnualKg > 0 ? embodiedAnnualKg.toLocaleString() : "—"}</td></tr>
  </table>
  <div class="note"><strong>Net carbon is reported on an operational basis</strong> (supply-chain emissions avoided by growing on-site, minus the design's own operational electricity at the stated grid factor). <strong>Embodied</strong> structural carbon is shown separately as a disclosed estimate amortized over a ${DESIGN_LIFE_YRS}-year design life and is <strong>not</strong> subtracted from the net — fold it into a whole-life assessment once below-grade and structural quantities are confirmed.</div>
  ${opportunity ? `<p style="font-size:12px;color:#86868B;margin-top:8px">Dynamic opportunity area: ${Math.round(opportunity.opportunity_area || 0).toLocaleString()} m² (MEP reserve ${opportunity.breakdown?.mep_reserve || 0} m², shadow ${Math.round((opportunity.breakdown?.shadow_pct || 0) * 100)}%, climate ×${opportunity.breakdown?.climate_factor ?? 1}).</p>` : ""}
  ${costingSection}
  <h2>Draft instances (${(blocks || []).length})</h2><table>
    <tr><th>System</th><th>Zone</th><th>Area</th><th>Height</th><th>Volume</th><th>Crops</th><th>Supplier</th><th>Overrides</th></tr>
    ${instanceRows || '<tr><td colspan="8">No instances drafted yet</td></tr>'}
  </table>
  <h2>Appendix — references used in this report</h2>
  <p style="font-size:11.5px;color:#6E6E73;margin-bottom:6px">Only the sources behind the systems, crops and assumptions used in this project are listed below.</p>
  <ul class="refs">${refRows}</ul>
  <footer><span>Resource Circularity Engine · www.resourcecircularity.com</span><span>Confidential — conceptual estimates, for client review</span></footer>
</body></html>`;

  // Print-to-PDF: render the brief inside a hidden iframe and open the browser print
  // dialog (user chooses "Save as PDF"). Avoids popup blockers and needs no PDF library.
  const safe = `Resource-Circularity-Engine-${(title || geo?.name || "Report").replace(/[^a-z0-9]+/gi, "-")}`.replace(/-+/g, "-").replace(/-$/, "").slice(0, 60);
  const iframe = document.createElement("iframe");
  iframe.setAttribute("data-testid", "report-print-frame");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  iframe.srcdoc = html;
  iframe.onload = () => {
    const win = iframe.contentWindow;
    if (!win) return;
    try { win.document.title = safe; } catch (e) { /* cross-origin guard */ }
    // Some browsers use the TOP document title as the "Save as PDF" filename; swap it during print
    // so it reads "Resource Circularity Engine…" (never the host app title), then restore it.
    const prevTopTitle = document.title;
    try { document.title = safe; } catch (e) { /* noop */ }
    win.focus();
    win.print();
    const cleanup = () => { try { document.title = prevTopTitle; } catch (e) { /* noop */ } setTimeout(() => iframe.remove(), 300); };
    if (win.matchMedia) {
      const mql = win.matchMedia("print");
      mql.addEventListener?.("change", (ev) => { if (!ev.matches) cleanup(); });
    }
    win.addEventListener("afterprint", cleanup);
    setTimeout(cleanup, 60000); // safety fallback
  };
  document.body.appendChild(iframe);
  return true;
}
